'use client';

import Link from '@carvana/ds-link';
import LoadingSpinner from '@carvana/ds-spinner';
import { useLoadableState } from '@carvana/merch-react-hooks';
import { useEffect } from 'react';
import ErrorIcon from '~/icons/Error';
import { ErrorBoundary } from '~/utilities/ErrorBoundary';

type OwnProps = {
  iframeUrl: string;
  vehicleId: number;
};

export default function AutoCheckClient({ iframeUrl, vehicleId }: OwnProps) {
  const {
    data: validatedUrl,
    hasError,
    setData,
    setError,
    setLoading
  } = useLoadableState('');

  useEffect(() => {
    setLoading();

    fetch(iframeUrl, { method: 'HEAD' })
      .then(res => {
        if (
          !res.ok ||
          new URLSearchParams(window.location.search).has('force-server-error')
        ) {
          throw new Error('Failed to load AutoCheck iframe');
        }

        setData(iframeUrl);
      })
      .catch(setError);
  }, [iframeUrl, setData, setError, setLoading]);

  return (
    <>
      <style>{`html {overflow: hidden;}`}</style>
      <ErrorBoundary>
        <div className="h-screen w-full bg-white">
          {validatedUrl && (
            <iframe className="relative h-full w-full" src={validatedUrl} />
          )}
          {hasError ? (
            <div className="flex h-full flex-col items-center justify-center gap-y-lg">
              <h2 className="t-heading-xl">Oops, there was a problem.</h2>
              <p className="t-body-lg">Please try again later</p>
              <ErrorIcon />
              <Link
                href={`/vehicle/${vehicleId}`}
                data-analytics-id="Back to Vehicle Details"
                data-analytics-attributes={{ location: 'AutoCheck' }}
                data-testid="back-to-vehicle-details"
              >
                Back to Vehicle Details Page
              </Link>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          )}
        </div>
      </ErrorBoundary>
    </>
  );
}
